import { KeyboardEvent } from "react";
import { AgendaItem } from "../../../services/AgendaService";
import { TranslationService } from "../../../services/TranslationService";
import {
  formatAgendaMoney,
  formatLastManagementLabel,
  formatSuggestedDate
} from "../utils/agendaFormat";
import { isCompleted } from "../utils/agendaUtils";
import AgendaActionPill from "./AgendaActionPill";
import AgendaLastManagement from "./AgendaLastManagement";
import AgendaPriorityBadge from "./AgendaPriorityBadge";

export type AgendaRowVariant = "summary" | "full";

interface AgendaItemRowProps {
  item: AgendaItem;
  variant: AgendaRowVariant;
  canEdit: boolean;
  onToggleCompleted: (item: AgendaItem) => void;
  onOpen: (item: AgendaItem) => void;
}

const AgendaItemRow = ({
  item,
  variant,
  canEdit,
  onToggleCompleted,
  onOpen
}: AgendaItemRowProps) => {
  const { translate } = TranslationService;
  const completed = isCompleted(item);
  const isFull = variant === "full";

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget && event.key === "Enter") {
      onOpen(item);
    }
  };

  return (
    <div
      className={
        "agenda-row" +
        (completed ? " agenda-row--completed" : "") +
        (isFull ? " agenda-row--full" : "")
      }
      role="button"
      tabIndex={0}
      onClick={() => onOpen(item)}
      onKeyDown={onKeyDown}
    >
      <input
        type="checkbox"
        className="form-check-input agenda-row__check"
        checked={completed}
        disabled={!canEdit}
        aria-label={item.nombre ?? ""}
        onClick={(event) => event.stopPropagation()}
        onChange={() => onToggleCompleted(item)}
      />
      <div className="agenda-row__main">
        <div className="agenda-row__title">
          <span className="agenda-row__name">{item.nombre}</span>
          <AgendaPriorityBadge priority={item.prioridad} />
        </div>
        <div className="agenda-row__meta">
          <AgendaLastManagement
            ultimaGestion={item.ultimaGestion}
            text={formatLastManagementLabel(item.ultimaGestion)}
          />
        </div>
        {isFull && (
          <div className="agenda-row__detail">{item.accionDetalle}</div>
        )}
      </div>
      <div className="agenda-row__amount">
        <span className="agenda-row__overdue">
          {formatAgendaMoney(item.deudaVencida)}
        </span>{" "}
        {translate.Of} {formatAgendaMoney(item.deuda)}
      </div>
      <div className="agenda-row__action">
        <AgendaActionPill action={item.accion} />
      </div>
      {isFull ? (
        <div className="agenda-row__side">
          <div className="agenda-row__suggested-label">
            {translate.AgendaSuggested}
          </div>
          <div className="agenda-row__suggested">
            {formatSuggestedDate(item.fechaSugerida)}
          </div>
          {completed && (
            <span className="agenda-badge agenda-badge--done">
              {translate.AgendaDoneBadge}
            </span>
          )}
        </div>
      ) : (
        <i
          className="fa-solid fa-arrow-right agenda-row__arrow"
          aria-hidden="true"
        />
      )}
    </div>
  );
};

export default AgendaItemRow;
